import { Option, SelectOption } from '@common/types/common';
import { Flag } from '@components/display/images';
import {
    ErrorAlertToast,
    ErrorToast,
    IErrorToastProps,
    IProgressToastProps,
    ProgressToast,
} from '@components/display/toasts';
import { IInfoToastProps, InfoAlertToast } from '@components/display/toasts/InfoToast';
import { ISuccessToastProps, SuccessAlertToast, SuccessToast } from '@components/display/toasts/SuccessToast';
import { IWarningToastProps, WarningAlertToast } from '@components/display/toasts/WarningToast';
import { ISODuration } from '@event/types/event';
import { AvatarProps, ListItemText, Typography, TypographyProps } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { Editor } from '@tiptap/react';
import { isApiResponseError } from '@utils/service';
import { de, enUS, es, fr, it, nl } from 'date-fns/locale';
import { FieldConfig } from 'formik/dist/Field';
import { ImageNodeAttributes, insertImages } from 'mui-tiptap';
import { ReactElement } from 'react';
import { Id, Slide, ToastOptions, toast } from 'react-toastify';

const defaultToasterProps: ToastOptions = {
    position: 'bottom-right',
    autoClose: 5000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'colored',
    transition: Slide,
    icon: false,
};

const toaster = (successProps: ISuccessToastProps, toastProps: ToastOptions) =>
    toast(<SuccessAlertToast {...successProps} />, { ...toastProps });
toaster.success = (successProps: ISuccessToastProps, toastProps = defaultToasterProps) =>
    toast.success(<SuccessAlertToast {...successProps} />, { ...toastProps });
toaster.failure = (errorProps: IErrorToastProps, toastProps = defaultToasterProps) =>
    toast.error(<ErrorAlertToast {...errorProps} />, { ...toastProps });
toaster.warning = (warningProps: IWarningToastProps, toastProps = defaultToasterProps) =>
    toast.warning(<WarningAlertToast {...warningProps} />, { ...toastProps });
toaster.info = (infoProps: IInfoToastProps, toastProps = defaultToasterProps) =>
    toast.info(<InfoAlertToast {...infoProps} />, { ...toastProps });

toaster.progress = (progressProps: IProgressToastProps, toastProps = defaultToasterProps): Id =>
    toast.info(<ProgressToast {...progressProps} />, {
        ...defaultToasterProps,
        ...toastProps,
        autoClose: false,
        progress: 0.01,
        hideProgressBar: false,
        progressClassName: 'bg-primary-500 opacity-100',
        type: 'info',
        // progressStyle: { background: 'var(--color-primary-600)', opacity: 1 },
    });

toaster.updateProgress = (toastId: Id, message: string, progress: number, toastProps = defaultToasterProps) => {
    toast.update(toastId, {
        ...toastProps,
        render: <ProgressToast msg={message} />,
        progress: Math.min(Math.max(progress, 0), 1), // Clamp between 0 and 1
        autoClose: false, // Ensure progress bar is controlled by progress value, not timer
        hideProgressBar: false,
        progressClassName: 'bg-primary-500 opacity-100',
        type: 'info',
        // progressStyle: { background: 'var(--color-primary-600)', opacity: 1 },
    });
};

toaster.completeProgress = (
    toastId: Id,
    message: string = 'Complete!',
    type: 'success' | 'error' = 'success',
    toastProps = defaultToasterProps
) => {
    if (type === 'success') {
        toast.update(toastId, {
            render: <SuccessToast msg={message} />,
            type: 'success',
            progressClassName: 'bg-success-500 opacity-100',
            ...toastProps,
        });
    } else {
        toast.update(toastId, {
            render: <ErrorToast msg={message} />,
            type: 'error',
            progressClassName: 'bg-error-500 opacity-100',
            ...toastProps,
        });
    }
};

const comparator = <T,>(a: Partial<T>, b: Partial<T>, orderBy: keyof T) => {
    const aValue = a[orderBy];
    const bValue = b[orderBy];

    if (aValue == null && bValue == null) return 0; // Both are missing
    if (aValue == null) return 1; // `a` is missing, should go to the end
    if (bValue == null) return -1; // `b` is missing, should go to the end

    // Both values are present, compare them
    if (aValue > bValue) return 1;
    if (aValue < bValue) return -1;

    return 0; // Equal values
};

const getFlagIcon = async (
    iconName: string,
    successCallback: (data: { default: string }) => void,
    failCallback: (data: string) => void
) => {
    import(`../assets/flags/${iconName}.svg`).then(successCallback, failCallback);
};

/**
 * Get the appropriate date-fns locale based on the current application locale
 * @param currentLocale The current application locale (e.g., 'es', 'en')
 * @returns The corresponding date-fns locale object
 */
const getDateFnsLocale = (currentLocale: string) => {
    switch (currentLocale) {
        case 'es':
            return es;
        case 'nl':
            return nl;
        case 'de':
            return de;
        case 'fr':
            return fr;
        case 'it':
            return it;
        case 'en':
        default:
            return enUS;
    }
};

const isDateStringValid = (dateStr: string) => !isNaN(new Date(dateStr).getTime());
const isDateValid = (date: Date) => !isNaN(date.getTime());
const isDateRangeValid = (range: DateRange<Date>): boolean =>
    range.every((v) => v !== null) &&
    [isDateValid(range[0] as Date), isDateValid(range[1] as Date)].some((v) => v === true);

const flip = (data: object) => Object.fromEntries(Object.entries(data).map(([key, value]) => [value, key]));

const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};

const nullEmptyStrings = (data: Record<string, unknown>) => {
    const newObj = { ...data };
    Object.keys(data).forEach(
        (k) => (newObj[k] = typeof data[k] === 'string' && (data[k] as string).trim() === '' ? null : data[k])
    );
    return newObj;
};

const getTextFromHTML = (html: string) => {
    const span = document.createElement('span');
    span.innerHTML = html;
    return span.textContent || span.innerText;
};

const YES_NO: SelectOption[] = [
    {
        label: 'Yes',
        value: 'true',
    },
    { label: 'No', value: 'false' },
];

const initialFormik = {
    values: {},
    errors: {},
    touched: {},
    isSubmitting: false,
    isValidating: false,
    submitCount: 0,
    setStatus: () => {},
    setErrors: () => {},
    setSubmitting: () => {},
    setTouched: () => Promise.resolve(),
    setValues: () => Promise.resolve(),
    setFieldValue: () => Promise.resolve(),
    setFieldError: () => {},
    setFieldTouched: () => Promise.resolve(),
    validateForm: () => Promise.resolve({}),
    validateField: () => Promise.resolve(),
    resetForm: () => Promise.resolve(),
    submitForm: () => Promise.resolve(),
    setFormikState: () => Promise.resolve(),
    handleSubmit: () => Promise.resolve(),
    handleReset: () => Promise.resolve(),
    handleBlur: () => {},
    handleChange: () => {},
    getFieldProps: (nameOrConfig: string | FieldConfig) => ({
        name: typeof nameOrConfig === 'string' ? nameOrConfig : nameOrConfig.name,
        value: {} as never,
        onChange: () => {},
        onBlur: () => {},
    }),
    getFieldMeta: () => ({ initialTouched: false, touched: false, value: {} as never }),
    getFieldHelpers: () => ({
        setError: () => {},
        setValue: () => Promise.resolve(),
        setTouched: () => Promise.resolve(),
    }),
    dirty: false,
    isValid: false,
    initialValues: {},
    initialErrors: {},
    initialTouched: {},
    registerField: () => {},
    unregisterField: () => {},
};

const allowedImageMimeTypes = [
    'blp',
    'bmp',
    'dib',
    'cur',
    'pcx',
    'dcx',
    'dds',
    'gif',
    'ico',
    'jfif',
    'jpe',
    'jpg',
    'jpeg',
    'jp2',
    'j2k',
    'jpc',
    'jpf',
    'jpx',
    'j2c',
    'icns',
    'im',
    'png',
    'apng',
    'pbm',
    'pgm',
    'ppm',
    'pnm',
    'psd',
    'tif',
    'tiff',
    'bw',
    'rgb',
    'rgba',
    'sgi',
    'ras',
    'tga',
    'icb',
    'vda',
    'vst',
    'webp',
    'xbm',
    'xpm',
];

const isAllowedImage = (file: File) => {
    const mimeType = (file.type || '').toLowerCase().split('image/')[1];
    return allowedImageMimeTypes.includes(mimeType);
};

const fileListToImageFiles = (fileList: FileList): File[] => Array.from(fileList).filter(isAllowedImage);

type MutationFunction<Args, Result, Error> = (arg: Args) => {
    unwrap: () => Promise<Result>;
} & Promise<{ data?: Result; error?: Error }>;

const getDeepestString = (obj: unknown): string | null => {
    if (typeof obj === 'string') {
        return obj;
    }

    if (Array.isArray(obj)) {
        return getDeepestString(obj[0]); // assume all items in array are similar; focus on the first item
    }

    if (typeof obj === 'object' && obj !== null) {
        for (const key of Object.keys(obj)) {
            const result = getDeepestString((obj as Record<string, unknown>)[key]);
            if (result) {
                return result;
            }
        }
    }

    return null;
};

const getError = (err: unknown) => {
    let msg: ReactElement;
    if (isApiResponseError(err)) {
        if (err.data.detail) {
            msg = <p>{err.data.detail}</p>;
        } else {
            msg = (
                <ul>
                    {Object.keys(err.data).map((errorKey) => {
                        const errorValue = err.data[errorKey];
                        let errorMsg = '';

                        if (Array.isArray(errorValue)) {
                            errorMsg = errorValue.map(getDeepestString).join(', ');
                        } else if (typeof errorValue === 'object') {
                            errorMsg = getDeepestString(errorValue) || 'Unknown error';
                        } else {
                            errorMsg = String(errorValue);
                        }

                        // If the key is 'non_field_error', display only the message, not the key
                        if (errorKey === 'non_field_errors') {
                            return <li key={'error-' + errorKey}>{errorMsg}</li>;
                        }
                        return (
                            <li key={'error-' + errorKey}>
                                {errorKey}: {errorMsg}
                            </li>
                        );
                    })}
                </ul>
            );
        }
    } else {
        console.error(err);
        msg = <p>Something went wrong! Please try again later.</p>;
    }

    return msg;
};

const handleError = (title: string, err: unknown, toastProps?: ToastOptions<unknown>) => {
    const error = getError(err);
    if (error) {
        if (Array.isArray(error)) {
            error.forEach((errorElement) =>
                toaster.failure(
                    {
                        title,
                        msg: errorElement,
                    },
                    toastProps
                )
            );
        } else {
            toaster.failure(
                {
                    title,
                    msg: error,
                },
                toastProps
            );
        }
    }
};

const checkBoundary = <T,>(value: T, boundary: T, comparator: (d: T, b: T) => boolean): boolean => {
    return comparator(value, boundary);
};

const isValidObjectKey = <T extends string>(key: string, obj: object): key is T => {
    return key in obj;
};

const getValidObjectKeys = <T extends string>(keys: string[], obj: object): T[] | false => {
    const validKeys: T[] = keys.map((k) => (isValidObjectKey<T>(k, obj) ? k : false)).filter((k) => !!k) as T[];
    return validKeys.length > 0 ? validKeys : false;
};

const isMac = /(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent);

async function readFileAsDataURL(file: File) {
    const result_base64 = await new Promise<string>((resolve) => {
        const fileReader = new FileReader();
        fileReader.onload = () => resolve(fileReader.result as string);
        fileReader.readAsDataURL(file);
    });

    return result_base64;
}

const handleImageUpload = async ({
    editor,
    files,
    postCall,
    params,
    position,
}: {
    editor: Editor | null;
    files: File[];
    /* eslint-disable @typescript-eslint/no-explicit-any */
    postCall: MutationFunction<any, { file: string }, unknown>;
    params?: any;
    /* eslint-enable @typescript-eslint/no-explicit-any */
    position?: number;
}): Promise<ImageNodeAttributes[]> => {
    try {
        if (!editor) {
            return [];
        }

        const attributesForImageFiles: ImageNodeAttributes[] = [];
        const promises =
            files?.map(async (file) => {
                try {
                    if (!isAllowedImage(file)) {
                        throw { msg: 'Unsupported file detected' };
                    } else if (file.size > 5 * 1024 * 1024) {
                        throw { msg: 'Image must be 5MB or less' };
                    }
                    const response = await postCall({ ...params, file }).unwrap();
                    attributesForImageFiles.push({
                        src: response.file,
                        alt: file.name,
                    });
                    return Promise.resolve();
                } catch (err) {
                    return Promise.reject(err);
                }
            }) ?? [];
        await Promise.all(promises);

        insertImages({
            images: attributesForImageFiles,
            editor: editor,
            position,
        });

        return attributesForImageFiles;
    } catch (err) {
        handleError('Error uploading images', err);
    } finally {
        return [];
    }
};

const stringToColor = (string: string) => {
    let hash = 0;
    let i;

    for (i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';

    for (i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }

    return color;
};

const stringAvatar = (
    name: string,
    typographyProps?: TypographyProps,
    color?: string,
    includeSecondInitial?: boolean
): AvatarProps => {
    if (!name) return {};
    const [firstName, lastName] = name.split(' ');
    return {
        sx: {
            bgcolor: color ? color : stringToColor(name),
        },
        children: (
            <Typography {...typographyProps} variant="body1">
                {firstName?.[0]?.toUpperCase()}
                {includeSecondInitial && lastName?.[0]?.toUpperCase()}
            </Typography>
        ),
    };
};

const typedKeys = <T extends object>(obj: T): (keyof T)[] => Object.keys(obj) as (keyof T)[];

const objectsAreEqual = <T extends object>(obj1: T, obj2: T): boolean => {
    if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
        return false;
    }

    const keys1: Array<keyof T> = typedKeys(obj1);
    const keys2: Array<keyof T> = typedKeys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
        if (!keys2.includes(key)) return false;

        const val1 = obj1[key];
        const val2 = obj2[key];

        const areObjects = typeof val1 === 'object' && val1 !== null && typeof val2 === 'object' && val2 !== null;

        if (areObjects && !objectsAreEqual(val1, val2)) return false;
        if (!areObjects && val1 !== val2) return false;
    }

    return true;
};

function arraysOfObjectsAreEqual<T extends object>(arr1?: T[], arr2?: T[]): boolean {
    if (arr1 === undefined || arr2 === undefined) return false;
    if (arr1.length !== arr2.length) return false;

    for (let i = 0; i < arr1.length; i++) {
        if (!objectsAreEqual(arr1[i], arr2[i])) return false;
    }

    return true;
}

function getYoutubeId(url: string) {
    const match = url.match(
        /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/#\s]{11})/
    );
    return match ? match[1] : null;
}

function parseISODuration(isoString: string): ISODuration {
    const regex = /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/;

    const match = isoString.match(regex);
    if (!match) throw new Error(`Invalid ISO 8601 duration: ${isoString}`);

    const [, years, months, weeks, days, hours, minutes, seconds] = match.map(Number);

    const duration: ISODuration = {
        years: years || 0,
        months: months || 0,
        weeks: weeks || 0,
        days: days || 0,
        hours: hours || 0,
        minutes: minutes || 0,
        seconds: seconds || 0,
    };

    // approximate ms conversion (average year/month)
    const msPerSecond = 1000;
    const msPerMinute = 60 * msPerSecond;
    const msPerHour = 60 * msPerMinute;
    const msPerDay = 24 * msPerHour;
    const msPerWeek = 7 * msPerDay;
    const msPerMonth = 30.4375 * msPerDay;
    const msPerYear = 365.25 * msPerDay;

    duration.totalMilliseconds =
        duration.years * msPerYear +
        duration.months * msPerMonth +
        duration.weeks * msPerWeek +
        duration.days * msPerDay +
        duration.hours * msPerHour +
        duration.minutes * msPerMinute +
        duration.seconds * msPerSecond;

    return duration;
}

function capitalizeWords(str: string) {
    return str.replace(/\b\w/g, (c) => c.toUpperCase());
}

function toCamelCase(str: string) {
    return str
        .split(/[\s_-]+/)
        .map((word, index) => {
            if (index === 0) {
                return word.toLowerCase();
            }
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join('');
}

function toHumanReadable(str: string, capitalize: boolean = false) {
    const readable = str
        // Split on capital letters, underscores, hyphens, and spaces
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .split(/[\s_-]+/)
        .map((word) => word.toLowerCase())
        .join(' ');

    if (!capitalize) {
        return readable;
    }

    // Capitalize first letter and letters after periods
    return readable.replace(/(^|\.\s*)([a-z])/g, (_match, prefix, letter) => {
        return prefix + letter.toUpperCase();
    });
}

function hasKeys<T extends object>(keys: (keyof T)[]) {
    return (obj: any): obj is T => !!obj && typeof obj === 'object' && keys.every((key) => key in obj);
}

const renderCountryOption = (item: Option) => (
    <ListItemText
        className="pt-1"
        slotProps={{
            primary: {
                className: '!flex items-center gap-2 !text-sm',
            },
        }}
    >
        <Flag flag={(item.value as string)?.toLowerCase()} />
        <p>{item.label}</p>
    </ListItemText>
);

const renderCountryValue = (value: string, options: Option[]) => {
    const option = options.find((opt) => opt.value === value);
    if (!option) return '';
    return renderCountryOption(option);
};

const safeParseNumber = (value: string | null, fallback: number) => {
    if (value === null) return fallback;
    const maybeNumber = Number(value);
    return Number.isNaN(maybeNumber) ? fallback : maybeNumber;
};

export {
    YES_NO,
    arraysOfObjectsAreEqual,
    capitalizeFirstLetter,
    capitalizeWords,
    checkBoundary,
    comparator,
    fileListToImageFiles,
    flip,
    getDateFnsLocale,
    getError,
    getFlagIcon,
    getTextFromHTML,
    getValidObjectKeys,
    getYoutubeId,
    handleError,
    handleImageUpload,
    hasKeys,
    initialFormik,
    isAllowedImage,
    isDateRangeValid,
    isDateStringValid,
    isDateValid,
    isMac,
    isValidObjectKey,
    nullEmptyStrings,
    objectsAreEqual,
    parseISODuration,
    readFileAsDataURL,
    renderCountryOption,
    renderCountryValue,
    stringAvatar,
    stringToColor,
    toCamelCase,
    toHumanReadable,
    toaster,
    typedKeys,
    safeParseNumber,
};
